<script setup>
import { ref } from "vue"
import SunRipple from "./SunRipple.vue"
import NewsletterDialog from "@/js/components/dialogs/NewsletterDialog.vue"

const dialogs = ref({ newsletter: { toggle: false } })
const email = ref("")

const linkedinUrl = import.meta.env.VITE_LINKEDIN_URL
const facebookUrl = import.meta.env.VITE_FACEBOOK_URL
const instagramUrl = import.meta.env.VITE_INSTAGRAM_URL
const twitterUrl = import.meta.env.VITE_TWITTER_URL
const mediumUrl = import.meta.env.VITE_MEDIUM_URL
const youtubeUrl = import.meta.env.VITE_YOUTUBE_URL
const githubUrl = import.meta.env.VITE_GITHUB_URL
</script>

<template>
  <footer>
    <div class="max-w-6xl mx-auto px-4 sm:px-6">
      <!-- Top area: Blocks -->
      <div
        class="grid sm:grid-cols-12 gap-8 py-8 md:py-12 border-t border-gray-200"
      >
        <!-- 1st block -->
        <div class="sm:col-span-12 lg:col-span-3">
          <div class="mb-2">
            <!-- Logo -->
            <router-link to="/" class="inline-block" aria-label="projetsolaire">
              <img class="w-20 h-20" src="@/images/logo.svg" />
            </router-link>
          </div>
          <div class="text-sm text-gray-600">
            <ul class="text-sm">
              <li class="mb-2">
                <router-link
                  to="/help/guides/installers/legal/mentions-legales"
                  class="text-gray-600 hover:text-gray-900 underline hover:bold transition duration-150 ease-in-out"
                  >{{ $t("footer.terms-and-conditions") }}</router-link
                >
              </li>
              <li class="mb-2">
                <router-link
                  to="/help/guides/installers/legal/declaration-de-confidentialite"
                  class="text-gray-600 hover:text-gray-900 underline hover:bold transition duration-150 ease-in-out"
                  >{{ $t("footer.privacy-policy") }}</router-link
                >
              </li>
            </ul>
          </div>
        </div>

        <!-- 2nd block -->
        <div class="sm:col-span-6 md:col-span-3 lg:col-span-2">
          <h6 class="text-gray-800 font-medium mb-2">
            {{ $t("footer.products-for-installers") }}
          </h6>
          <ul class="text-sm">
            <li class="mb-2">
              <router-link
                to="/installer/find-new-clients"
                class="text-gray-600 hover:text-gray-900 transition duration-150 ease-in-out"
                >{{ $t("footer.find-clients") }}</router-link
              >
            </li>
            <li class="mb-2">
              <router-link
                to="/installer/administration"
                class="text-gray-600 hover:text-gray-900 transition duration-150 ease-in-out"
                >{{ $t("footer.administration") }}</router-link
              >
            </li>
            <li class="mb-2">
              <router-link
                to="/installer/digital-sales-tools"
                class="text-gray-600 hover:text-gray-900 transition duration-150 ease-in-out"
                >{{ $t("footer.digital-sales-tools") }}</router-link
              >
            </li>
            <li class="mb-2">
              <router-link
                to="/installer"
                class="text-gray-600 hover:text-gray-900 transition duration-150 ease-in-out"
                >{{ $t("footer.technology") }}</router-link
              >
            </li>
            <li class="mb-2">
              <router-link
                to="/resale-agreements"
                class="text-gray-600 hover:text-gray-900 transition duration-150 ease-in-out"
                >{{ $t("footer.resale-agreements") }}</router-link
              >
            </li>
            <!--
               <li class="mb-2">
              <router-link
                to="/virtual-battery"
                class="text-gray-600 hover:text-gray-900 transition duration-150 ease-in-out"
                >{{ $t("footer.virtual-battery-providers") }}</router-link
              >
            </li>
            -->
            <!--
               <li class="mb-2">
              <router-link
                to="/architect"
                class="text-gray-600 hover:text-gray-900 transition duration-150 ease-in-out"
                >{{ $t("footer.architect") }}</router-link
              >
            </li>
            -->
          </ul>
        </div>

        <!-- 3rd block -->
        <div class="sm:col-span-6 md:col-span-3 lg:col-span-2">
          <h6 class="text-gray-800 font-medium mb-2">
            {{ $t("footer.resources") }}
          </h6>
          <ul class="text-sm">
            <li class="mb-2">
              <router-link
                to="/blog/series?series=introductions"
                class="text-gray-600 hover:text-gray-900 transition duration-150 ease-in-out"
                >{{ $t("footer.guide") }}</router-link
              >
            </li>
            <li class="mb-2">
              <router-link
                to="/blog/series"
                class="text-gray-600 hover:text-gray-900 transition duration-150 ease-in-out"
                >{{ $t("footer.blog") }}</router-link
              >
            </li>
            <li class="mb-2">
              <router-link
                to="/help/installer"
                class="text-gray-600 hover:text-gray-900 transition duration-150 ease-in-out"
                >{{ $t("footer.documentation") }}</router-link
              >
            </li>
            <li class="mb-2">
              <router-link
                to="/support"
                class="text-gray-600 hover:text-gray-900 transition duration-150 ease-in-out"
                >{{ $t("footer.support-centre") }}</router-link
              >
            </li>
          </ul>
        </div>

        <!-- 4th block -->
        <div class="sm:col-span-6 md:col-span-3 lg:col-span-2">
          <h6 class="text-gray-800 font-medium mb-2">
            {{ $t("footer.enterprise") }}
          </h6>
          <ul class="text-sm">
            <li class="mb-2">
              <router-link
                to="/about"
                class="text-gray-600 hover:text-gray-900 transition duration-150 ease-in-out"
                >{{ $t("footer.about-us") }}</router-link
              >
            </li>
            <li class="mb-2">
              <router-link
                to="/career"
                class="text-gray-600 hover:text-gray-900 transition duration-150 ease-in-out"
                >{{ $t("footer.careers") }}</router-link
              >
            </li>
            <li class="mb-2">
              <router-link
                :to="{
                  name: 'SupportSchedule',
                  params: { slug: 'partnerships' },
                }"
                class="text-gray-600 hover:text-gray-900 transition duration-150 ease-in-out"
                >{{ $t("footer.partners") }}</router-link
              >
            </li>
            <li class="mb-2">
              <router-link
                to="/support/schedule/press"
                class="text-gray-600 hover:text-gray-900 transition duration-150 ease-in-out"
                >{{ $t("footer.press") }}</router-link
              >
            </li>
          </ul>
        </div>

        <!-- 5th block -->
        <div class="sm:col-span-6 md:col-span-3 lg:col-span-3">
          <h6 class="text-gray-800 font-medium mb-2">
            {{ $t("footer.newsletter") }}
          </h6>
          <RouterLink
            class="text-gray-600 text-sm block mb-2"
            :to="{
              name: 'BlogSeries',
              query: { series: 'supplySideNewsletter' },
            }"
            >{{
              $t("blog.series.topics-series.supplySideNewsletter")
            }}</RouterLink
          >
          <p class="text-sm text-gray-600 mb-4">
            {{ $t("footer.subscribe-newsletter") }}
          </p>
          <form @submit.prevent>
            <div class="flex flex-wrap mb-4">
              <div class="w-full">
                <label class="block text-sm sr-only" for="newsletter">{{
                  $t("common.email")
                }}</label>
                <div class="relative flex items-center max-w-xs">
                  <input
                    id="newsletter"
                    v-model.trim="email"
                    type="email"
                    class="form-input w-full text-gray-800 px-3 py-2 pr-12 text-sm"
                    :placeholder="$t('common.your-email')"
                    required
                  />
                  <button
                    type="submit"
                    class="absolute inset-0 left-auto"
                    aria-label="Subscribe"
                    @click="dialogs.newsletter.toggle = true"
                  >
                    <span
                      class="absolute inset-0 right-auto w-px -ml-px my-2 bg-gray-300"
                      aria-hidden="true"
                    ></span>
                    <svg
                      class="w-3 h-3 fill-current bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-400 mx-3 shrink-0"
                      viewBox="0 0 12 12"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.707 5.293L7 .586 5.586 2l3 3H0v2h8.586l-3 3L7 11.414l4.707-4.707a1 1 0 000-1.414z"
                        fill-rule="nonzero"
                      />
                    </svg>
                  </button>
                </div>
                <!-- Success message -->
                <!-- <p class="mt-2 text-green-600 text-sm">Thanks for subscribing!</p> -->
              </div>
            </div>
          </form>
        </div>
      </div>

      <!-- Bottom area -->
      <div
        class="md:flex md:items-center md:justify-between py-4 md:py-8 border-t border-gray-200"
      >
        <!-- Social links -->
        <ul class="flex mb-4 md:order-1 md:ml-4 md:mb-0">
          <li class="ml-4">
            <a
              :href="linkedinUrl"
              target="_blank"
              aria-label="LinkedIn"
              class="flex justify-center items-center text-gray-600 hover:text-gray-900 bg-white hover:bg-white-100 rounded-full shadow transition duration-150 ease-in-out"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="w-8 h-8 fill-current p-1"
              >
                <g id="SVGRepo_bgCarrier-linkedin" stroke-width="0"></g>
                <g
                  id="SVGRepo_tracerCarrier-linkedin"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                ></g>
                <g id="SVGRepo_iconCarrier">
                  <path
                    d="M18.72 3.99997H5.37C5.19793 3.99191 5.02595 4.01786 4.86392 4.07635C4.70189 4.13484 4.55299 4.22471 4.42573 4.34081C4.29848 4.45692 4.19537 4.59699 4.12232 4.75299C4.04927 4.909 4.0077 5.07788 4 5.24997V18.63C4.01008 18.9901 4.15766 19.3328 4.41243 19.5875C4.6672 19.8423 5.00984 19.9899 5.37 20H18.72C19.0701 19.9844 19.4002 19.8322 19.6395 19.5761C19.8788 19.32 20.0082 18.9804 20 18.63V5.24997C20.0029 5.08247 19.9715 4.91616 19.9078 4.76122C19.8441 4.60629 19.7494 4.466 19.6295 4.34895C19.5097 4.23191 19.3672 4.14059 19.2108 4.08058C19.0544 4.02057 18.8874 3.99314 18.72 3.99997ZM9 17.34H6.67V10.21H9V17.34ZM7.89 9.12997C7.72741 9.13564 7.5654 9.10762 7.41416 9.04768C7.26291 8.98774 7.12569 8.89717 7.01113 8.78166C6.89656 8.66615 6.80711 8.5282 6.74841 8.37647C6.6897 8.22474 6.66301 8.06251 6.67 7.89997C6.66281 7.73567 6.69004 7.57169 6.74995 7.41854C6.80986 7.26538 6.90112 7.12644 7.01787 7.01063C7.13463 6.89481 7.2743 6.80468 7.42793 6.74602C7.58157 6.68735 7.74577 6.66145 7.91 6.66997C8.07259 6.66431 8.2346 6.69232 8.38584 6.75226C8.53709 6.8122 8.67431 6.90277 8.78887 7.01828C8.90344 7.13379 8.99289 7.27174 9.05159 7.42347C9.1103 7.5752 9.13699 7.73743 9.13 7.89997C9.13719 8.06427 9.10996 8.22825 9.05005 8.3814C8.99014 8.53456 8.89888 8.6735 8.78213 8.78931C8.66537 8.90513 8.5257 8.99526 8.37207 9.05392C8.21843 9.11259 8.05423 9.13849 7.89 9.12997ZM17.34 17.34H15V13.44C15 12.51 14.67 11.87 13.84 11.87C13.5822 11.8722 13.3313 11.9541 13.1219 12.1045C12.9124 12.2549 12.7546 12.4664 12.67 12.71C12.605 12.8926 12.5778 13.0865 12.59 13.28V17.34H10.29V10.21H12.59V11.21C12.7945 10.8343 13.0988 10.5225 13.4694 10.3089C13.84 10.0954 14.2624 9.98848 14.69 9.99997C16.2 9.99997 17.34 11 17.34 13.13V17.34Z"
                  ></path>
                </g>
              </svg>
            </a>
          </li>
          <li class="ml-4">
            <a
              :href="twitterUrl"
              target="_blank"
              class="flex justify-center items-center text-gray-600 hover:text-gray-900 bg-white hover:bg-white-100 rounded-full shadow transition duration-150 ease-in-out"
              aria-label="Twitter"
            >
              <svg
                class="w-8 h-8 fill-current"
                viewBox="0 0 32 32"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M24 11.5c-.6.3-1.2.4-1.9.5.7-.4 1.2-1 1.4-1.8-.6.4-1.3.6-2.1.8-.6-.6-1.5-1-2.4-1-1.7 0-3.2 1.5-3.2 3.3 0 .3 0 .5.1.7-2.7-.1-5.2-1.4-6.8-3.4-.3.5-.4 1-.4 1.7 0 1.1.6 2.1 1.5 2.7-.5 0-1-.2-1.5-.4 0 1.6 1.1 2.9 2.6 3.2-.3.1-.6.1-.9.1-.2 0-.4 0-.6-.1.4 1.3 1.6 2.3 3.1 2.3-1.1.9-2.5 1.4-4.1 1.4H8c1.5.9 3.2 1.5 5 1.5 6 0 9.3-5 9.3-9.3v-.4c.7-.5 1.3-1.1 1.7-1.8z"
                />
              </svg>
            </a>
          </li>
          <li class="ml-4">
            <a
              :href="facebookUrl"
              target="_blank"
              class="flex justify-center items-center text-gray-600 hover:text-gray-900 bg-white hover:bg-white-100 rounded-full shadow transition duration-150 ease-in-out"
              aria-label="Facebook"
            >
              <svg
                class="w-8 h-8 fill-current"
                viewBox="0 0 32 32"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.023 24L14 17h-3v-3h3v-2c0-2.7 1.672-4 4.08-4 1.153 0 2.144.086 2.433.124v2.821h-1.67c-1.31 0-1.563.623-1.563 1.536V14H21l-1 3h-2.72v7h-3.257z"
                />
              </svg>
            </a>
          </li>
          <li class="ml-4">
            <a
              :href="instagramUrl"
              target="_blank"
              class="flex justify-center items-center text-gray-600 hover:text-gray-900 bg-white hover:bg-white-100 rounded-full shadow transition duration-150 ease-in-out"
              aria-label="Instagram"
            >
              <svg
                viewBox="0 0 32 32"
                xmlns="http://www.w3.org/2000/svg"
                class="w-8 h-8 fill-current p-1"
              >
                <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                <g
                  id="SVGRepo_tracerCarrier"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                ></g>
                <g id="SVGRepo_iconCarrier">
                  <path
                    d="M20.445 5h-8.891A6.559 6.559 0 0 0 5 11.554v8.891A6.559 6.559 0 0 0 11.554 27h8.891a6.56 6.56 0 0 0 6.554-6.555v-8.891A6.557 6.557 0 0 0 20.445 5zm4.342 15.445a4.343 4.343 0 0 1-4.342 4.342h-8.891a4.341 4.341 0 0 1-4.341-4.342v-8.891a4.34 4.34 0 0 1 4.341-4.341h8.891a4.342 4.342 0 0 1 4.341 4.341l.001 8.891z"
                  ></path>
                  <path
                    d="M16 10.312c-3.138 0-5.688 2.551-5.688 5.688s2.551 5.688 5.688 5.688 5.688-2.551 5.688-5.688-2.55-5.688-5.688-5.688zm0 9.163a3.475 3.475 0 1 1-.001-6.95 3.475 3.475 0 0 1 .001 6.95zM21.7 8.991a1.363 1.363 0 1 1-1.364 1.364c0-.752.51-1.364 1.364-1.364z"
                  ></path>
                </g>
              </svg>
            </a>
          </li>
          <li class="ml-4">
            <a
              :href="youtubeUrl"
              target="_blank"
              class="flex justify-center items-center text-gray-600 hover:text-gray-900 bg-white hover:bg-white-100 rounded-full shadow transition duration-150 ease-in-out"
              aria-label="Youtube"
            >
              <svg
                id="Layer_1"
                class="w-8 h-8 fill-current p-1"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink"
                viewBox="0 0 461.001 461.001"
                xml:space="preserve"
                fill="#000000"
              >
                <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                <g
                  id="SVGRepo_tracerCarrier"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                ></g>
                <g id="SVGRepo_iconCarrier">
                  <g>
                    <path
                      d="M365.257,67.393H95.744C42.866,67.393,0,110.259,0,163.137v134.728 c0,52.878,42.866,95.744,95.744,95.744h269.513c52.878,0,95.744-42.866,95.744-95.744V163.137 C461.001,110.259,418.135,67.393,365.257,67.393z M300.506,237.056l-126.06,60.123c-3.359,1.602-7.239-0.847-7.239-4.568V168.607 c0-3.774,3.982-6.22,7.348-4.514l126.06,63.881C304.363,229.873,304.298,235.248,300.506,237.056z"
                    ></path>
                  </g>
                </g>
              </svg>
            </a>
          </li>

          <li class="ml-4">
            <a
              :href="mediumUrl"
              target="_blank"
              class="flex justify-center items-center text-gray-600 hover:text-gray-900 bg-white hover:bg-white-100 rounded-full shadow transition duration-150 ease-in-out"
              aria-label="Medium"
            >
              <svg
                class="w-8 h-8 fill-current p-1"
                viewBox="0 -55 256 256"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink"
                preserveAspectRatio="xMidYMid"
              >
                <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                <g
                  id="SVGRepo_tracerCarrier"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                ></g>
                <g id="SVGRepo_iconCarrier">
                  <g>
                    <path
                      d="M72.2009141,1.42108547e-14 C112.076502,1.42108547e-14 144.399375,32.5485469 144.399375,72.6964154 C144.399375,112.844284 112.074049,145.390378 72.2009141,145.390378 C32.327779,145.390378 0,112.844284 0,72.6964154 C0,32.5485469 32.325326,1.42108547e-14 72.2009141,1.42108547e-14 Z M187.500628,4.25836743 C207.438422,4.25836743 223.601085,34.8960455 223.601085,72.6964154 L223.603538,72.6964154 C223.603538,110.486973 207.440875,141.134463 187.503081,141.134463 C167.565287,141.134463 151.402624,110.486973 151.402624,72.6964154 C151.402624,34.9058574 167.562834,4.25836743 187.500628,4.25836743 Z M243.303393,11.3867175 C250.314,11.3867175 256,38.835526 256,72.6964154 C256,106.547493 250.316453,134.006113 243.303393,134.006113 C236.290333,134.006113 230.609239,106.554852 230.609239,72.6964154 C230.609239,38.837979 236.292786,11.3867175 243.303393,11.3867175 Z"
                    ></path>
                  </g>
                </g>
              </svg>
            </a>
          </li>
          <li class="ml-4">
            <a
              :href="githubUrl"
              target="_blank"
              class="flex justify-center items-center text-gray-600 hover:text-gray-900 bg-white hover:bg-white-100 rounded-full shadow transition duration-150 ease-in-out"
              aria-label="Github"
            >
              <svg
                class="w-8 h-8 fill-current"
                viewBox="0 0 32 32"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16 8.2c-4.4 0-8 3.6-8 8 0 3.5 2.3 6.5 5.5 7.6.4.1.5-.2.5-.4V22c-2.2.5-2.7-1-2.7-1-.4-.9-.9-1.2-.9-1.2-.7-.5.1-.5.1-.5.8.1 1.2.8 1.2.8.7 1.3 1.9.9 2.3.7.1-.5.3-.9.5-1.1-1.8-.2-3.6-.9-3.6-4 0-.9.3-1.6.8-2.1-.1-.2-.4-1 .1-2.1 0 0 .7-.2 2.2.8.6-.2 1.3-.3 2-.3s1.4.1 2 .3c1.5-1 2.2-.8 2.2-.8.4 1.1.2 1.9.1 2.1.5.6.8 1.3.8 2.1 0 3.1-1.9 3.7-3.7 3.9.3.4.6.9.6 1.6v2.2c0 .2.1.5.6.4 3.2-1.1 5.5-4.1 5.5-7.6-.1-4.4-3.7-8-8.1-8z"
                />
              </svg>
            </a>
          </li>
        </ul>

        <!-- Copyrights note -->
        <div class="text-sm text-gray-600 mr-4">
          {{ $t("footer.copyright") }}
        </div>
      </div>

      <SunRipple class="invisible lg:visible" />
    </div>
    <NewsletterDialog
      v-if="dialogs.newsletter.toggle"
      v-model="dialogs.newsletter.toggle"
      :email="email"
    />
  </footer>
</template>
